/**
 * @homerunner-next/widget-registry — the single UI-free source of truth for
 * system widget keywords, legacy aliases, kinds, and categories (#1283).
 *
 * Before this package, three copies of the keyword list existed by
 * construction (`packages/homerunner-embeddables/hr.ts`, the dashboard's
 * `types/widget.ts`, the renderer's `types/widget.ts`) plus a creatable/alias
 * registry in the protected app, pinned equal only by a regex-over-source
 * parity test (#1150). Every one of those now derives from here; a keyword
 * added anywhere else is a bug.
 *
 * Rules for adding a widget:
 *  - append the canonical keyword to SYSTEM_WIDGET_KEYWORDS (alphabetical);
 *  - give it a category in WIDGET_CATEGORY_BY_KEYWORD (the compiler enforces
 *    coverage);
 *  - rebuild the catalog (`pnpm --dir packages/homerunner-embeddables run
 *    build:catalog` — --dir, not --filter, which no-ops on a rename) — CI
 *    fails on a registry change without a catalog rebuild.
 *
 * This package is UI-free on purpose: no React, no Zod, no imports from the
 * embeddables' widget implementations. Config schemas stay canonical in
 * `@homerunner/embeddables/widgets/{keyword}/config`; the catalog build
 * (embeddables `scripts/widget-catalog.ts`) resolves them by that convention.
 */
import { PLUGIN_WIDGET_PREFIX } from "@homerunner-next/widget-core/urls";

export { PLUGIN_WIDGET_PREFIX };

/**
 * Canonical system widget keywords — the creatable set. Alphabetical, legacy
 * aliases excluded. Coverage counts (catalog, adapters, tests) derive from
 * this array's length, never a hard-coded number.
 */
export const SYSTEM_WIDGET_KEYWORDS = [
  "amenities",
  "booking",
  "calendar",
  "calendar-v2",
  "checkout",
  "checkout-layout",
  "confirmation",
  "confirmation-layout",
  "content",
  "description",
  "destination-search",
  "explorer",
  "gallery",
  "listings-layout",
  "multi-calendar",
  "promo-bar",
  "property-actions",
  "property-card",
  "property-details-layout",
  "property-finder",
  "property-map",
  "property-meta",
  "property-title",
  "property-video",
  "rate-comparison",
  "related-properties",
  "reviews",
  "saved-panel",
  "search-bar",
  "search-results",
  "section-nav",
  "spaces",
  "specs",
  "split-cost",
] as const;

export type SystemWidgetKeyword = (typeof SYSTEM_WIDGET_KEYWORDS)[number];

/**
 * Legacy keyword spellings still present in stored Central rows. They must
 * keep RESOLVING (config forms, playground, list badges) but must never be
 * OFFERED or auto-created — iterating the canonical set alone guarantees
 * that.
 */
export const LEGACY_WIDGET_ALIASES = {
} as const satisfies Record<string, SystemWidgetKeyword>;

export type LegacyWidgetAlias = keyof typeof LEGACY_WIDGET_ALIASES;

/** Every keyword a stored system-widget row may carry: canonical + legacy. */
export const STORED_WIDGET_KEYWORDS = [
  ...SYSTEM_WIDGET_KEYWORDS,
  ...(Object.keys(LEGACY_WIDGET_ALIASES) as LegacyWidgetAlias[]),
].sort() as readonly (SystemWidgetKeyword | LegacyWidgetAlias)[];

export type StoredWidgetKeyword = SystemWidgetKeyword | LegacyWidgetAlias;

/** Plugin widget keyword — `plugin:{slug}` (#1150: runtime-valid everywhere). */
export type PluginWidgetKeyword = `${typeof PLUGIN_WIDGET_PREFIX}${string}`;

/** The full keyword type for a `Widget["keyword"]` field. */
export type WidgetKeyword = StoredWidgetKeyword | PluginWidgetKeyword;

/** Resolve a stored keyword to its canonical spelling (identity for non-aliases). */
export function canonicalWidgetKeyword(keyword: StoredWidgetKeyword): SystemWidgetKeyword;
export function canonicalWidgetKeyword(keyword: string): string;
export function canonicalWidgetKeyword(keyword: string): string {
  return (LEGACY_WIDGET_ALIASES as Record<string, string>)[keyword] ?? keyword;
}

export const isSystemWidgetKeyword = (keyword: string): keyword is SystemWidgetKeyword =>
  (SYSTEM_WIDGET_KEYWORDS as readonly string[]).includes(keyword);

export const isStoredWidgetKeyword = (keyword: string): keyword is StoredWidgetKeyword =>
  isSystemWidgetKeyword(keyword) || keyword in LEGACY_WIDGET_ALIASES;

export const isPluginWidgetKeyword = (keyword: string): keyword is PluginWidgetKeyword =>
  keyword.startsWith(PLUGIN_WIDGET_PREFIX);

/**
 * Structural kind. The `-layout` suffix convention is shared with
 * `isLayoutWidget` in the dashboard's ssr-widgets and the runtime's layout
 * CSR renderer; this is its one exported home.
 */
export type WidgetKind = "widget" | "layout";

export const isLayoutKeyword = (keyword: string): boolean => keyword.endsWith("-layout");

export const widgetKindOf = (keyword: string): WidgetKind =>
  isLayoutKeyword(keyword) ? "layout" : "widget";

/** Display/catalog grouping. */
export type WidgetCategory = "property" | "booking" | "browse";

/**
 * Category per canonical keyword. Total on purpose — adding a keyword without
 * a category is a compile error, so catalog coverage cannot drift.
 */
export const WIDGET_CATEGORY_BY_KEYWORD: Record<SystemWidgetKeyword, WidgetCategory> = {
  gallery: "property",
  description: "property",
  specs: "property",
  amenities: "property",
  spaces: "property",
  reviews: "property",
  "property-title": "property",
  "property-meta": "property",
  "property-map": "property",
  "property-card": "property",
  // Property-page action row (#1693): All Properties, Share, Save.
  "property-actions": "property",
  // Property-page walkthrough video from a custom field (#1699).
  "property-video": "property",
  "related-properties": "property",
  "property-details-layout": "property",
  // Property-page chrome: links to the PDP's own section widgets.
  "section-nav": "property",
  calendar: "booking",
  // #920: the typographic new-gen calendar, an opt-in alternative to `calendar`.
  "calendar-v2": "booking",
  "multi-calendar": "booking",
  booking: "booking",
  checkout: "booking",
  confirmation: "booking",
  "rate-comparison": "booking",
  "checkout-layout": "booking",
  "confirmation-layout": "booking",
  "split-cost": "booking",
  explorer: "browse",
  // Search Bar Pro keywords (#1263): thin registrations over the explorer —
  // the same browse family, one facet of the explorer each.
  "search-bar": "browse",
  "search-results": "browse",
  "listings-layout": "browse",
  // Landing-page copy block (#1264); sits with the browse/listings family it
  // is built for.
  content: "browse",
  "property-finder": "browse",
  "saved-panel": "browse",
  // Page-wide announcement chrome; none of the three categories fit cleanly.
  // "browse" is the placeholder (its Tier-B CTA deep-links into browse/search);
  // flagged in #1144 for the taxonomy owner.
  "promo-bar": "browse",
  // Standalone search bar over the feed's destinations.
  "destination-search": "browse",
};

/** null for plugin:* and unknown keywords (rendered muted, no category dot). */
export const widgetCategoryOf = (keyword: string): WidgetCategory | null => {
  const canonical = canonicalWidgetKeyword(keyword);
  return isSystemWidgetKeyword(canonical) ? WIDGET_CATEGORY_BY_KEYWORD[canonical] : null;
};
